/**
 * FE cache of ms-casino GetApplicationConfigurations rows.
 *
 * Login-session scoped (no TTL):
 * - Fetch once right after successful login → Redux + module + sessionStorage
 * - Page reload within the same login session hydrates from sessionStorage (no network)
 * - Cleared on logout; next login always refetches
 * - Fallback months1 via getConfig when missing / invalid / fetch failed
 */

import { APPLICATION_CONFIG_SESSION_KEY } from 'common/sessionKeys';

export type ApplicationConfigRow = {
  uuid: string;
  type: string;
  name: string;
  value: string;
  valueDataType: string;
  /** Server-parsed value; null when invalid. */
  parsedValue: string | number | boolean | null;
};

/** TimeFrameValueEnum keys accepted for valueDataType === 'timeframe' (server mirror). */
export const TIMEFRAME_CONFIG_VALUES = [
  'hours1',
  'hours4',
  'hours8',
  'hours24',
  'weeks1',
  'months1',
  'sinceLastJackpot',
] as const;

/** @deprecated use APPLICATION_CONFIG_SESSION_KEY — kept for legacy localStorage purge */
export const APPLICATION_CONFIG_LS_KEY = 'slotcheck:applicationConfigurations';

export { APPLICATION_CONFIG_SESSION_KEY };

const TIMEFRAME_SET = new Set<string>(TIMEFRAME_CONFIG_VALUES);

export type SessionApplicationConfigurations = {
  rows: ApplicationConfigRow[];
};

let byName: Map<string, ApplicationConfigRow> = new Map();
let loaded = false;
/** True after this JS runtime finished hydrate-or-fetch for the current login. */
let sessionLoadComplete = false;
let sessionNetworkInFlight = false;
/** Bumped on each beginApplicationConfigLoginSession so hooks refetch when auth stays true (e.g. emulate). */
let loginEpoch = 0;
type LoginEpochListener = () => void;
const loginEpochListeners = new Set<LoginEpochListener>();

export function getApplicationConfigLoginEpoch(): number {
  return loginEpoch;
}

export function subscribeApplicationConfigLoginEpoch(listener: LoginEpochListener): () => void {
  loginEpochListeners.add(listener);
  return () => {
    loginEpochListeners.delete(listener);
  };
}

function sessionStorageOrNull(storage?: Storage | null): Storage | null {
  if (storage !== undefined) return storage;
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function isValidRow(value: unknown): value is ApplicationConfigRow {
  if (!value || typeof value !== 'object') return false;
  const r = value as ApplicationConfigRow;
  return typeof r.name === 'string' && r.name.length > 0;
}

/** Remove legacy localStorage copy from the prior TTL design (best-effort). */
export function purgeLegacyApplicationConfigLocalStorage(
  local?: Storage | null
): void {
  try {
    const ls =
      local !== undefined
        ? local
        : typeof window !== 'undefined'
          ? window.localStorage
          : null;
    ls?.removeItem(APPLICATION_CONFIG_LS_KEY);
  } catch {
    // ignore
  }
}

export function readSessionApplicationConfigurations(
  storage?: Storage | null
): SessionApplicationConfigurations | null {
  const ss = sessionStorageOrNull(storage);
  if (!ss) return null;
  try {
    const raw = ss.getItem(APPLICATION_CONFIG_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionApplicationConfigurations | ApplicationConfigRow[];
    // Accept bare array or { rows }
    const rowsRaw = Array.isArray(parsed) ? parsed : parsed?.rows;
    if (!Array.isArray(rowsRaw)) return null;
    const rows = rowsRaw.filter(isValidRow);
    return { rows };
  } catch {
    return null;
  }
}

export function writeSessionApplicationConfigurations(
  rows: ApplicationConfigRow[] | null | undefined,
  storage?: Storage | null
): void {
  const ss = sessionStorageOrNull(storage);
  if (!ss) return;
  try {
    const payload: SessionApplicationConfigurations = {
      rows: Array.isArray(rows) ? rows : [],
    };
    ss.setItem(APPLICATION_CONFIG_SESSION_KEY, JSON.stringify(payload));
  } catch {
    // quota / private mode — ignore
  }
}

export function clearSessionApplicationConfigurations(storage?: Storage | null): void {
  const ss = sessionStorageOrNull(storage);
  if (!ss) return;
  try {
    ss.removeItem(APPLICATION_CONFIG_SESSION_KEY);
  } catch {
    // ignore
  }
}

export type ApplicationConfigLoadPlan =
  | { action: 'use_session'; rows: ApplicationConfigRow[] }
  | { action: 'fetch' };

/**
 * Within a login session: sessionStorage hit → hydrate, no network.
 * Missing → fetch once. No TTL.
 */
export function planApplicationConfigLoad(
  storage?: Storage | null
): ApplicationConfigLoadPlan {
  const session = readSessionApplicationConfigurations(storage);
  if (session) return { action: 'use_session', rows: session.rows };
  return { action: 'fetch' };
}

export function setApplicationConfigurations(rows: ApplicationConfigRow[] | null | undefined): void {
  byName = new Map();
  for (const row of rows || []) {
    if (row?.name) byName.set(row.name, row);
  }
  loaded = true;
}

export function clearApplicationConfigurations(): void {
  byName = new Map();
  loaded = false;
  sessionLoadComplete = false;
  sessionNetworkInFlight = false;
}

/** Clears memory + sessionStorage (logout / tests). */
export function clearApplicationConfigurationsAll(storage?: Storage | null): void {
  clearApplicationConfigurations();
  clearSessionApplicationConfigurations(storage);
  purgeLegacyApplicationConfigLocalStorage();
}

/**
 * Call at the start of a successful login so this session will refetch
 * (replaces any prior login-session cache). Bumps loginEpoch so loaders
 * re-run even when auth was already AUTHENTICATED (emulate).
 */
export function beginApplicationConfigLoginSession(storage?: Storage | null): void {
  clearApplicationConfigurationsAll(storage);
  loginEpoch += 1;
  loginEpochListeners.forEach((l) => l());
}

export function areApplicationConfigurationsLoaded(): boolean {
  return loaded;
}

export function isApplicationConfigSessionLoadComplete(): boolean {
  return sessionLoadComplete;
}

export function markApplicationConfigSessionLoadComplete(): void {
  sessionLoadComplete = true;
  sessionNetworkInFlight = false;
}

export function isApplicationConfigSessionNetworkInFlight(): boolean {
  return sessionNetworkInFlight;
}

export function markApplicationConfigSessionNetworkInFlight(v: boolean = true): void {
  sessionNetworkInFlight = v;
}

export function getApplicationConfigRow(name: string): ApplicationConfigRow | undefined {
  return byName.get(name);
}

/**
 * Parsed config value for `name`, or `fallback` when missing / invalid / not loaded.
 */
export function getConfig<T = string | number | boolean>(name: string, fallback: T): T {
  const row = byName.get(name);
  if (!row) return fallback;
  if (row.parsedValue === null || row.parsedValue === undefined) return fallback;
  return row.parsedValue as T;
}

/** Client-side parse mirror (tests / offline). Invalid → null. */
export function parseApplicationConfigValue(
  value: unknown,
  valueDataType: unknown
): string | number | boolean | null {
  const raw = value === null || value === undefined ? '' : String(value).trim();
  const dt = valueDataType === null || valueDataType === undefined ? '' : String(valueDataType).trim().toLowerCase();
  if (!raw || !dt) return null;
  switch (dt) {
    case 'string':
      return raw;
    case 'number': {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
    case 'decimal(18,2)': {
      const n = Number(raw);
      if (!Number.isFinite(n)) return null;
      return Math.round(n * 100) / 100;
    }
    case 'boolean': {
      const lower = raw.toLowerCase();
      if (lower === 'true' || lower === '1') return true;
      if (lower === 'false' || lower === '0') return false;
      return null;
    }
    case 'timeframe':
      return TIMEFRAME_SET.has(raw) ? raw : null;
    default:
      return null;
  }
}

export const EGM_DETAIL_GRAPH_DEFAULT_TIMEFRAME_NAME = 'egmDetailGraphDefaultTimeframe';
export const EGM_DETAIL_GRAPH_DEFAULT_TIMEFRAME_FALLBACK = 'months1';

/**
 * Resolve EGM Details graph default timeframe string.
 * Invalid / missing → months1.
 */
export function getEgmDetailGraphDefaultTimeframe(
  fallback: string = EGM_DETAIL_GRAPH_DEFAULT_TIMEFRAME_FALLBACK
): string {
  const v = getConfig<string>(EGM_DETAIL_GRAPH_DEFAULT_TIMEFRAME_NAME, fallback);
  if (typeof v === 'string' && TIMEFRAME_SET.has(v)) return v;
  return TIMEFRAME_SET.has(fallback) ? fallback : EGM_DETAIL_GRAPH_DEFAULT_TIMEFRAME_FALLBACK;
}

/**
 * Map a timeframe value to a Details graph tab id among `tabs`.
 * Not in tabs / invalid → months1 tab id (6) or first months1 match.
 */
export function resolveEgmDetailGraphDefaultTimeframeId(
  tabs: Array<{ timeframeId: number; value: string }>,
  configValue?: string | null
): number {
  const monthsTab = tabs.find((t) => t.value === 'months1');
  const fallbackId = monthsTab?.timeframeId ?? 6;
  const raw =
    configValue === undefined || configValue === null
      ? getEgmDetailGraphDefaultTimeframe()
      : String(configValue).trim();
  if (!TIMEFRAME_SET.has(raw)) return fallbackId;
  const hit = tabs.find((t) => t.value === raw);
  return hit ? hit.timeframeId : fallbackId;
}
